import {
  DialogActionButton,
  LoadingOverlay,
  saveCallbackStatus,
  TextField,
  Toggle,
} from '@finpay-development/shared-components';
import {Grid, MenuItem, Typography} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {useFormik} from 'formik';
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import * as Yup from 'yup';
import { patientRecordUtils } from './patient-record-utils';
import {
  clearStatus,
  setPatientEncounters,
  setSelectedEncounter,
  clearEncounter,
} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-slice';
import {showErrorStatus, showStatus} from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import {statesList} from '@finpay-development/shared-platform/lib/esm/shared/misc/us-states';
import {
  PhoneType,
} from '@finpay-development/shared-platform/lib/esm/shared/model/primary-contact';
import { Workflow } from '@finpay-development/shared-platform/lib/esm/shared/model/client';
import {State} from '@finpay-development/shared-platform/lib/esm/shared/model/state';
import {RootState} from '../../../shared/state/root-reducer';
import {AppDispatch} from '../../../shared/state/store';
import {Utils} from '@finpay-development/shared-platform/lib/esm/shared/utils';
import {completePatientModalSchema} from '@finpay-development/shared-platform/lib/esm/shared/validation/schemas';
import {
  emptyFinPassPatientModel,
  IocConfig
} from '@finpay-development/shared-platform/lib/esm/admissions-advisor/models/patient';
import {
  loadSavedEstimatesListv3,
  saveSelectedEstimate,
  saveSelectedEstimateSelfPay
} from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk';
import {
  createPatientIoc,
} from '@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-patient-thunk';
import {admissionsAdvisorUtils} from '@finpay-development/shared-platform/lib/esm/admissions-advisor/utils/admission-advisor-utils';
import { EstimateValidationService } from '../../utils/estimate-validation';
import {
  payorRisk,
  PayorRisk,
  payorRiskNameLookup,
  TimingRisk,
  timingRisk,
  timingRiskNameLookup,
} from '@finpay-development/shared-platform/lib/esm/shared/model/timing-and-payor-risk';
import {EstDetails} from './estimation-details';
import {
  workFlowStatus,
} from '@finpay-development/shared-platform/lib/esm/patient/components/models/workflow-status';
import {
  TakePaymentView,
} from '../../../shared/components/take-payment-modal/take-payment-view-v3';
import {find as _find, set as _set} from 'lodash';
import {patientService} from '@finpay-development/shared-platform/lib/esm/patient/services/patient-service';
import {Estimate, Facility, Id, PayorPlan} from '@finpay-estimation/estimation-types';
import {savePatient, savePatientStatus} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import { loadAndSetRateAsPayorPlan } from '../../../estimates/utils/payor-plan-utils';
import { isSelfPayPayor } from '../../../shared/utils/insurance-utils';

type ClientWithFacilities = {
  clientId?: number;
  clientFacilities?: { facilityId: number; facilityName: string }[];
};

function resolveFacilityForEstimate(
  estimate: { clientId?: number; facilityId?: number } | null | undefined,
  allClients: ClientWithFacilities[] | undefined,
  fallbackFacility?: { facilityId?: number; facilityName?: string }
): Facility {
  const facilityId = estimate?.facilityId ?? fallbackFacility?.facilityId;
  if (facilityId == null) {
    return (fallbackFacility ?? {}) as Facility;
  }
  if (estimate?.clientId != null && allClients?.length) {
    const client = allClients.find(
      (c) => Number(c.clientId) === Number(estimate.clientId)
    );
    const match = client?.clientFacilities?.find(
      (f) => Number(f.facilityId) === Number(facilityId)
    );
    if (match) {
      return { facilityId: match.facilityId, facilityName: match.facilityName } as Facility;
    }
  }
  return {
    facilityId: Number(facilityId),
    facilityName: fallbackFacility?.facilityName,
  } as Facility;
}

interface EstCompletePatientRecordModalProps {
  open: boolean;
  handlePatientRecordModalCancel: () => void;
  handlePatientRecordModalSubmit: (estimation:Estimate) => void;
}

interface NameValue {
  name: string;
  value: string;
}

export function EstCompletePatientRecordModal(
  props: EstCompletePatientRecordModalProps
) {
  const {
    open,
    handlePatientRecordModalCancel,
    handlePatientRecordModalSubmit,
  } = props;

  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const [displayPFRTotalError, setDisplayPFRTotalError] = useState(false);
  const [selectedState, setSelectedState] = useState<State>();
  const [isAddNew, setIsAddNew] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [stripePaymentReadyStatus, setStripePaymentReadyStatus] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [finadvisorTakePayment, setFinadvisorTakePayment] = useState<boolean | undefined>(false);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [isBillingEngineV3, setIsBillingEngineV3] = useState<boolean>(false);

  const PatientTitles: NameValue[] = [
    { name: "Mrs.", value: "Mrs." },
    { name: "Mr.", value: "Mr." },
    { name: "Ms.", value: "M." },
  ];

  const state = {
    clients: useSelector(
        (state: RootState) =>
            state.implementationContext.implementationSpecialistClient.filteredByIsActive
    ),
    estimatorState: useSelector(
      (state: RootState) =>
        state.estimatorContext.selectedEstimate
    ),
    vobState: useSelector(
      (state: RootState) =>
        state.vobContext.selectedVob
    ),
    rateAsPayorPlan: useSelector(
      (state: RootState) =>
        state.estimatorContext.rateAsPayorPlan
    ) as PayorPlan | null,
    vobPatientState: useSelector(
      (state: RootState) => state.admissionsAdvisorContext.vobPatientContext
    ),
    errorMessage: useSelector(
      (state: RootState) =>
        state.patientContext.errorMessage
    ),
    isLoadingPatientIoc: useSelector(
      (state: RootState) =>
        state.patientContext.isLoading.createPatientIoc
    ),
    iocSaveStatus: useSelector(
      (state: RootState) =>
        state.patientContext.completePatientModalIOCSaveStatus
    ),
    saveFinPassPatientStatus: useSelector(
      (state: RootState) =>
        state.admissionsAdvisorContext.vobPatientContext.saveFinPassPatientStatus
    ),
    reduxRateAsPayorMap: useSelector((state: RootState) => state.estimatorContext.rateAsPayorMap) || {},
    reduxMasterPayors: useSelector((state: RootState) => state.adminContext.adminConfigurationContext?.payersMap) || new Map(),
    finPassErrorMessage: useSelector(
      (state: RootState) =>
        state.admissionsAdvisorContext.vobPatientContext.errorMessage
    ),
    savedEstimatesStatusState: useSelector(
      (state: RootState) =>
        state.admissionsAdvisorContext.estimatorContext.isLoading.saveEstimateStatus
    ),
    savedEstimationsState: useSelector(
      (state: RootState) =>
          state.estimatorContext.savedEstimatesList
    ),
    savedEstimatesLoadingState: useSelector(
        (state: RootState) =>
            state.estimatorContext.isLoading.savedEstimatesList
    ),
    stripeToken: useSelector(
      (state: RootState) =>
        state.patientContext?.patientTakePaymentTokens?.token
    ),
    selectedIOC: useSelector((state: RootState) => state.patientContext.selectedEncounter),
    allClients: useSelector(
      (state: RootState) =>
      state.implementationContext?.implementationSpecialistClient.allClientsWithFacillities
    ),
    selectedPatientEncounters: useSelector(
      (state: RootState) =>
        state.patientContext?.selectedPatient.patientEncounters
    ),
    selectedFacilityRiskData: useSelector(
      (state: RootState) => 
        state.admissionsAdvisorContext.riskAssessmentContext.selectedFacilityRiskData

    ),
    selectedPatient: useSelector(
      (state: RootState) => state.patientContext.selectedPatient
    ),
    userProfile: useSelector(
      (state: RootState) => state.userContext.userProfile
    ),
    payors: useSelector(
      (state: RootState) => state.adminContext.adminConfigurationContext?.payers
    ),
    selectedPatientCurrentInsurance: useSelector(
      (state: RootState) => state.patientContext.selectedPatientCurrentInsurance
    ),
    payersMap: useSelector(
      (state: RootState) => state.adminContext.adminConfigurationContext.payersMap
    )
  };

  const {
    estimatorState, vobState, vobPatientState,
    isLoadingPatientIoc, iocSaveStatus, errorMessage,saveFinPassPatientStatus,
    finPassErrorMessage, savedEstimatesLoadingState, savedEstimatesStatusState, savedEstimationsState,
    stripeToken, selectedIOC, clients, allClients, selectedPatientEncounters, selectedFacilityRiskData, selectedPatient, userProfile, rateAsPayorPlan, payors, selectedPatientCurrentInsurance, payersMap
  } = state;

  useEffect(() => {
    if (vobPatientState?.patient?.advisorPatientBody?.primaryAddress?.state) {
      const stateToSave = statesList?.find(
        (usState) => usState?.stateCode === vobPatientState?.patient?.advisorPatientBody?.primaryAddress.state.stateCode
      );
      setSelectedState(stateToSave)
    }

    const currentClient = _find(clients, (client) => (client?.clientId === estimatorState?.clientId));
    setFinadvisorTakePayment(currentClient?.finadvisorTakePayment);
    setIsBillingEngineV3(currentClient?.billingEngineVersion === 'V3');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getWorkflow = ({pfrAmt, payAtFacilityAmt, payNowAmt, selectedIOCWorkflowStatusId}: Record<string, any>)=> {
      // Determine workflow based on amount to be paid at facility for VT dashboard.
      // Todo: Include payment amount for "Take Payment Now" functionality when built out for Levels of Care.
      const remainingAmt = pfrAmt - payNowAmt;
      if (payAtFacilityAmt && remainingAmt === payAtFacilityAmt) {
        return workFlowStatus.preconvert.find(workflow =>
          workflow.workflowStatus.workflowStatusName === "Follow Up Needed" &&
          workflow.workflowSubStatus.workflowSubStatusName === 'Pending Payment at Facility'
        );
      } else if (payAtFacilityAmt && remainingAmt - payAtFacilityAmt > 0) {
        // Case: Remaining PFR amount to be paid both at the facility and through a payment plan.
        return workFlowStatus.preconvert.find(workflow =>
          workflow.workflowStatus.workflowStatusName === "New" &&
          workflow.workflowSubStatus.workflowSubStatusName === 'Pending Payment at Facility'
        );
      }else if(selectedIOCWorkflowStatusId === 20){

        // "Not Managed" IOCs are set to "New - None" initially
        return workFlowStatus.preconvert.find((workflow: any)=>{
          return workflow.workflowStatus.workflowStatusName === "New" && workflow.workflowSubStatus.workflowSubStatusName === "None"
        })

      }
    };

  const requireEstimateEncounterId = (...candidateIds: unknown[]): number => {
    for (const id of candidateIds) {
      if (id != null && id !== '' && Number.isFinite(Number(id)) && Number(id) > 0) {
        return Number(id);
      }
    }
    throw new Error('Estimate save requires a linked patient encounter');
  };

  const savePreviouslyUnmanagedIocWorkflowStatus = async ({
    encounterId,
    pfrAmt,
    workflowStatusId,
  }: {
    encounterId?: number;
    pfrAmt: number;
    workflowStatusId?: number;
  }) => {
    if (workflowStatusId !== 20 || !encounterId) {
      return;
    }

    const workflow = getWorkflow({
      pfrAmt,
      payAtFacilityAmt: Number(completePatientFormik.values.payAtFacilityAmt),
      payNowAmt: Number(completePatientFormik.values.payNowAmt),
      selectedIOCWorkflowStatusId: 20,
    });

    if (!workflow?.workflowId) {
      return;
    }

    const workflowToSave: Omit<Workflow, 'workflowName'> = {
      workflowId: workflow.workflowId,
      workflowStatus: {
        workflowStatusId: workflow.workflowStatus.workflowStatusId,
        workflowStatusDesc: workflow.workflowStatus.workflowStatusName || '',
      },
      workflowSubStatus: {
        workflowSubStatusId: workflow.workflowSubStatus.workflowSubStatusId,
        workflowSubStatusDesc: workflow.workflowSubStatus.workflowSubStatusName || '',
      },
    };

    await dispatch(
      savePatientStatus({
        workFlow: workflowToSave as Workflow,
        encounterId: Number(encounterId),
        patientId: Number(selectedPatient.patientId),
      })
    );
  };

  const syncEstimateEncounterWorkflowFromApi = async (
    encounterId: number,
    pfrAmt: number
  ) => {
    if (!selectedPatient?.patientId) {
      throw new Error('Missing patientId');
    }

    const freshResponse = await patientService.getPatientInstanceOfCare({
      patientId: selectedPatient.patientId,
      encounterId,
    });
    if (freshResponse.hasErrors) {
      throw new Error(freshResponse.errorMessage || 'Failed to fetch patient encounter');
    }

    let encounter = freshResponse.entity;
    if (Number(encounter?.workflow?.workflowStatus?.workflowStatusId) === 20) {
      await savePreviouslyUnmanagedIocWorkflowStatus({
        encounterId,
        pfrAmt,
        workflowStatusId: 20,
      });

      const refreshedResponse = await patientService.getPatientInstanceOfCare({
        patientId: selectedPatient.patientId,
        encounterId,
      });
      if (!refreshedResponse.hasErrors) {
        encounter = refreshedResponse.entity;
      }
    }

    dispatch(setSelectedEncounter(encounter));
    return encounter;
  };

  // const makePreConvertedPayment = async (totalPaymentAmount: number, encounterId: number, clientCrm: ClientCrm[], ownerId: string | null) => {

  //   const paymentProgram = patientRecordUtils.mapToPatientPaymentProgram(completePatientFormik.values, selectedIOC, estimatorState);
  //   const paymentProgramResponse = await dispatch(savePatientPaymentProgram(
  //     {
  //       paymentProgram: paymentProgram,
  //       patientId: selectedPatient?.patientId,
  //       encounterId: selectedIOC?.patientEncounterId!,
  //       showNotify: false,
  //     }
  //   ))
  //   const scheduleId = (paymentProgramResponse.payload as PatientPaymentProgram).patientPaymentSchedule.patientPaymentScheduleId;
  //   console.log("scheduleId", scheduleId);
  //   debugger

  //   const paymentInfo = patientRecordUtils.mapToPayment(
  //     totalPaymentAmount,
  //     encounterId,
  //     selectedPatient.patientId,
  //     scheduleId as number,
  //     completePatientFormik.values,
  //     stripeToken!,
  //     channelId as number,
  //     ownerId
  //   );

  //   // Add ownerId to the payment info if available and taking payment
  //   if (ownerId) {
  //     paymentInfo.ownerId = Number(ownerId);
  //   }

  //   // call RT udpate
  //   const isIntegrationEnabled = admissionsAdvisorUtils.checkIntegrationFeatureFlag(clientCrm);
  //   if (isIntegrationEnabled) {
  //       patientService.integrationUpdate({
  //           patientEncounterId: encounterId,
  //         crmTypeSlug:
  //               clientCrm.length > 0 ? clientCrm[0].crmType.crmTypeSlug : undefined,
  //               patientId: selectedPatient?.patientId,
  //       });
  //   }

  //   return paymentInfo;
  // }

  // const mapToPatientEncounter = (patientEncounter: any) => {
  //     const {
  //         eceSent,
  //         authorizationDocumentStatus,
  //         pfrEstimateId,
  //         isWarmTransferCompleted,
  //         convertDt,
  //         typeOfCare,
  //         finClearanceStatus,
  //         vobDate,
  //         client,
  //         hasChampions,
  //         ...rest
  //     } = patientEncounter;

  //     const realTimeEncounterPayload: any = {
  //         ...(eceSent && {eceSent}),
  //         ...(pfrEstimateId && {pfrEstimateId}),
  //         ...(isWarmTransferCompleted && {isWarmTransferCompleted}),
  //         ...(convertDt && {convertDt}),
  //         ...(typeOfCare?.typeOfCareId && {typeOfCare}),
  //         ...(finClearanceStatus && {finClearanceStatus}),
  //         ...(vobDate && {vobDate}),
  //         hasChampions: hasChampions || false,
  //         ...rest,
  //     };

  //     delete realTimeEncounterPayload.authorizationDocumentStatus;
  //     delete realTimeEncounterPayload.client;
  //     delete realTimeEncounterPayload.pfrEstimateId;

  //     return realTimeEncounterPayload;
  // };

  const saveIOC = async () => {
    // Check if estimate exists
    if (!estimatorState) {
      dispatch(showErrorStatus('No estimate available for IOC creation'));
      throw new Error('No estimate available for IOC creation');
    }

    const pfrSummary: any = estimatorState?.summary;
    const pfrAmt = pfrSummary?.totalPFR ?? 0;
    const foundTimingRisk = timingRisk.find((timing: TimingRisk) => timing.timingRiskId === completePatientFormik.values.timingRisk);
    const foundPayorRisk = payorRisk.find((payor: PayorRisk) => payor.payorRiskId === completePatientFormik.values.payorRisk);
    const levelsOfCare = estimatorState?.facilityLevelOfCare?.map(loc => loc.facilityLevelOfCareName).join(", ");
    const truncLevelsOfCare = levelsOfCare?.length! > 128 ? levelsOfCare?.slice(0, 128) : levelsOfCare;
    const isIOCPreviouslyUnmanaged = selectedIOC?.workflow?.workflowStatus?.workflowStatusId === 20
    const workflow: Record<string, any> | undefined = getWorkflow({
      pfrAmt,
      payAtFacilityAmt: Number(completePatientFormik.values.payAtFacilityAmt),
      payNowAmt: Number(completePatientFormik.values.payNowAmt),
      selectedIOCWorkflowStatusId: selectedIOC?.workflow?.workflowStatus?.workflowStatusId,
    })
    const finClearanceStatus = "New";

    const iocConfig: IocConfig = {
      paramId: 0,
      patientId: selectedPatient?.patientId,
      clientId: estimatorState?.clientId! as number, // num 
      facilityId: estimatorState?.facilityId as number,
      pfrAmt: pfrAmt,
      pfrEstimateId: estimatorState?.estimateId as number,
      timingRisk: foundTimingRisk,
      payorRisk: foundPayorRisk,
      finClearanceStatus: finClearanceStatus,
      levelOfCare: truncLevelsOfCare,
      // typeOfCare: defaultTypeOfCare,
      ...workflow && {
        workflow
      }
    }

    iocConfig.totalSurchargeRate = admissionsAdvisorUtils.newEstimateCalculateTotalSurchargeRate(
        estimatorState?.facilityLevelOfCare!
    );

    // create/update instance of care
    const iocResponse = await dispatch(createPatientIoc(iocConfig));

    console.log("iocResponse", iocResponse);
    console.log("selectedIOC", selectedIOC);
    let updatedIOC = iocResponse?.payload

    console.log("updatedIOC", updatedIOC);

    //Send previously unmanaged IOC to Mobile Queue
    if(isIOCPreviouslyUnmanaged){
      const response = await patientService.sendInstanceOfCareToMobileQueue(
          updatedIOC?.patientEncounterId
      );
      if (response.hasErrors) {
        console.error(`Error sending Unmanaged IOC to Mobile Queue : `, response.errorMessage)
        dispatch(showErrorStatus(`Error sending Unmanaged IOC to Mobile Queue: ${response.errorMessage}`));
      }
      if(response.entity?.failureMessages.length ===0){
        //FinMobile conditions have all passed
        updatedIOC = response.entity?.patientEncounter;
        // ask stephen about this case
        dispatch(setSelectedEncounter(response.entity?.patientEncounter))
      }
    }

    dispatch(setSelectedEncounter(updatedIOC));
    //if selectedPatientEncounters is 1 and encounter id = 0 and patient id = 0
    if (selectedPatientEncounters && selectedPatientEncounters.length === 1 && selectedPatientEncounters[0].patientEncounterId === 0 && selectedPatientEncounters[0].patientId === 0) {
      dispatch(setPatientEncounters([{...updatedIOC}]));
    }

    return updatedIOC;
  }

  const saveCurrentEstimate = async (updatedIOC: any) => {
    let estimatorStateCopy = Utils.deepClone(estimatorState);
    estimatorStateCopy.patientEncounterId = updatedIOC?.patientEncounterId;
    estimatorStateCopy = setEstimatorDescription(estimatorStateCopy);

    if (!estimatorStateCopy?.estimateId) {
      throw new Error('Missing estimateId - cannot save estimate');
    }

    if (!vobState) {
      throw new Error('Missing vobState - cannot save estimate');
    }

    const estimateFacility = resolveFacilityForEstimate(
      estimatorStateCopy,
      allClients,
      vobState.facility
    );

    // Load and set rateAsPayorPlan before saving the estimate
    await loadAndSetRateAsPayorPlan(
      vobState.client,
      estimateFacility,
      dispatch,
      state.reduxMasterPayors,
      vobState.payor,
      vobState.plan,
      state.reduxRateAsPayorMap
    );

    const putEstimateBody = admissionsAdvisorUtils.mapToNewEstimate(
      vobState.vobId,
      vobState.client?.clientId!,
      estimateFacility.facilityId!,
      estimatorStateCopy,
      selectedPatient.patientId,
      updatedIOC?.patientEncounterId
    );

    putEstimateBody.patientId = selectedPatient?.patientId;
    if (rateAsPayorPlan) {
      putEstimateBody.rateAsPayorPlan = rateAsPayorPlan;
    }

    await dispatch(
      isSelfPay || estimatorStateCopy?.isPrivatePay
        ? saveSelectedEstimateSelfPay({
            estimateId: estimatorStateCopy.estimateId,
            estimate: putEstimateBody,
          })
        : saveSelectedEstimate({
            estimateId: estimatorStateCopy.estimateId,
            estimate: putEstimateBody,
          })
    );
  }


  const updateAllEstimatesWithoutIOC = async (
    patientEncounterId: number,
    excludeEstimateId?: Id
  ) => {
    if (!savedEstimationsState || !Array.isArray(savedEstimationsState)) {
      return;
    }

    const estimatesWithoutIOC = savedEstimationsState.filter((est: any) => {
      const estimateBody = est.estimateBody || est;
      return !estimateBody.patientEncounterId && est.pfrEstimateId;
    });

    if (estimatesWithoutIOC.length === 0) {
      return;
    }

    for (const est of estimatesWithoutIOC) {
      try {
        const estAny = est as any;
        const estimateBody = estAny.estimateBody || est;
        const estimateId = estAny.pfrEstimateId || estAny.estimateId;

        if (
          excludeEstimateId != null &&
          Number(estimateId) === Number(excludeEstimateId)
        ) {
          continue;
        }

        if (!estimateId || !vobState) {
          continue;
        }

        const facilityId =
          estimateBody.facilityId ?? vobState.facility?.facilityId;

        const updateBody = admissionsAdvisorUtils.mapToNewEstimate(
          vobState.vobId,
          vobState.client?.clientId!,
          facilityId!,
          estimateBody,
          estimateBody.advisorPatientId || selectedPatient?.patientId || 0,
          patientEncounterId.toString()
        );

        updateBody.patientId = selectedPatient?.patientId;
        if (rateAsPayorPlan) {
          updateBody.rateAsPayorPlan = rateAsPayorPlan;
        }

        const isSelfPayOrPrivatePay =
          estimateBody.isSelfPay || estimateBody.isPrivatePay;
        
        await dispatch(
          isSelfPayOrPrivatePay
            ? saveSelectedEstimateSelfPay({
                estimateId: estimateId,
                estimate: updateBody,
              })
            : saveSelectedEstimate({
                estimateId: estimateId,
                estimate: updateBody,
              })
        );
      } catch (error) {
        const estAny = est as any;
        console.error(`Error updating estimate ${estAny.pfrEstimateId || estAny.estimateId}:`, error);
      }
    }
  }

  // Remove the loadAndSetRateAsPayorPlan function since we're now importing it


  async function handleSave() {
    // First, check if a payment token is needed and present
    if (completePatientFormik.values.isPayNow && !stripePaymentReadyStatus && !stripeToken) {
      dispatch(showErrorStatus("Payment token is missing. Please input a valid payment method."))
      return;
    }
    
    try {
      setIsLoading(true);
      setEnableSaveButton(false);
      dispatch(clearStatus());

      // Check if the estimate has a patientEncounterId
      if (estimatorState?.patientEncounterId) {
        // Update existing encounter with the formik values
        const foundTimingRisk = timingRisk.find((timing: TimingRisk) => timing.timingRiskId === completePatientFormik.values.timingRisk);
        const foundPayorRisk = payorRisk.find((payor: PayorRisk) => payor.payorRiskId === completePatientFormik.values.payorRisk);
        
        // Get PFR amount from estimator state
        const pfrSummary: any = estimatorState?.summary;
        const pfrAmt = pfrSummary?.totalPFR ?? 0;
        
        const levelsOfCare = estimatorState?.facilityLevelOfCare?.map(loc => loc.facilityLevelOfCareName).join(", ");
        const truncLevelsOfCare = levelsOfCare && levelsOfCare.length > 128 ? levelsOfCare.slice(0, 128) : levelsOfCare;
        const totalSurchargeRate = admissionsAdvisorUtils.newEstimateCalculateTotalSurchargeRate(
          estimatorState?.facilityLevelOfCare
        );
        
        let riskClassId: number | undefined;
        const timingRiskValue = completePatientFormik.values.timingRisk;
        const payorRiskValue = completePatientFormik.values.payorRisk;
        const timingRiskName = timingRiskValue ? timingRiskNameLookup(timingRiskValue) : undefined;
        const payorRiskName = payorRiskValue ? payorRiskNameLookup(payorRiskValue) : undefined;
        
        if (timingRiskName && payorRiskName && estimatorState?.facilityId) {
          const riskClassResponse = await patientService.getPatientEncounterRiskClass(
            Number(estimatorState.facilityId),
            false,
            timingRiskName,
            payorRiskName,
            pfrAmt
          );
          
          if (!riskClassResponse.hasErrors && riskClassResponse.entity?.riskClassId) {
            riskClassId = riskClassResponse.entity.riskClassId;
          }
        }
        
        // Update existing encounter with partial object
        const promiseResponse = await patientService.saveIOCWithPartialObject(
          {
            timingRiskId: foundTimingRisk?.timingRiskId,
            payorRiskId: foundPayorRisk?.payorRiskId,
            isWarmTransferCompleted: completePatientFormik.values.isWarmTransfer === 'Warm Transfer',
            pfrEstimateId: estimatorState.estimateId,
            pfrAmt: pfrAmt,
            facilityId: estimatorState?.facilityId,
            totalSurchargeRate,
            ...(truncLevelsOfCare && { levelOfCare: truncLevelsOfCare }),
            ...(riskClassId && { riskClassId })
          },
          Number(estimatorState.patientEncounterId)
        );

        if (promiseResponse.hasErrors) {
          throw new Error(promiseResponse.errorMessage || "Failed to update patient encounter");
        }

        const encounterId = requireEstimateEncounterId(estimatorState.patientEncounterId);
        const response = {
          entity: await syncEstimateEncounterWorkflowFromApi(encounterId, pfrAmt),
        };
        
        // Create notes if needed (noteText OR at facility payment)
        if (completePatientFormik.values.noteText || 
            (completePatientFormik.values.isPayAtFacility && Number(completePatientFormik.values.payAtFacilityAmt) > 0)) {
          await patientRecordUtils.createNotes(
            dispatch, 
            completePatientFormik.values, 
            response.entity, 
            selectedPatient
          );
        }
        
        // Handle payment if needed
        if (completePatientFormik.values.isPayNow) {
          const clientItem = allClients?.find(client => client.clientId === estimatorState?.clientId);
          const clientCrm = clientItem?.clientCrm || [];
          
          const paymentSuccess = await patientRecordUtils.handlePayment(
            dispatch,
            completePatientFormik.values,
            response.entity,
            selectedPatient,
            clientCrm,
            stripeToken!,
            channelId,
            handlePatientRecordModalCancel,
            setOwnerId,
            response.entity
          );
          
          if (!paymentSuccess) {
            setIsLoading(false);
            setEnableSaveButton(true);
            return;
          }
          
          // Update patient contact email AFTER successful payment
          if (completePatientFormik.values.receiptEmail) {
            await updatePatientContactEmail(completePatientFormik.values.receiptEmail);
          }
        } else {
          // Handle integration updates if not making payment
          const clientItem = allClients?.find(client => client.clientId === estimatorState?.clientId);
          const clientCrm = clientItem?.clientCrm || [];
          
          await patientRecordUtils.handleIntegrationUpdates(
            response.entity,
            clientCrm,
            selectedPatient
          );
        }
        
        // Update payment program
        await patientRecordUtils.updatePaymentProgram(dispatch, response.entity);
        
        // Stop loading state and close modal
        setIsLoading(false);
        setEnableSaveButton(true);
        if (estimatorState) {
          handlePatientRecordModalSubmit(estimatorState);
        }
        
        // Fetch the estimates list again before showing the success status
        // This ensures the estimates list is refreshed with the latest data
        if (selectedPatient?.patientId) {
          await dispatch(loadSavedEstimatesListv3({
            paramId: -2,
            patientId: Number(selectedPatient?.patientId),
          }));
        }
        
        dispatch(showStatus(`Successfully updated instance of care`));
      } else {
        // Create a new encounter
        let createdIOC = await saveIOC();

          console.log("updating estimate with encounter id", createdIOC?.patientEncounterId);
          await saveCurrentEstimate(createdIOC);

          const pfrSummary: any = estimatorState?.summary;
          const pfrAmt = pfrSummary?.totalPFR ?? 0;
          const encounterId = requireEstimateEncounterId(
            createdIOC?.patientEncounterId,
            estimatorState?.patientEncounterId
          );
          createdIOC = await syncEstimateEncounterWorkflowFromApi(encounterId, pfrAmt);

          if (createdIOC?.patientEncounterId) {
            await updateAllEstimatesWithoutIOC(
              createdIOC.patientEncounterId,
              estimatorState?.estimateId
            );
          }
          
          // Update the estimate in state with the patientEncounterId
          await patientRecordUtils.updateEstimator(
            dispatch,
            estimatorState as Estimate,
            createdIOC,
          );
          
          // Create notes if needed (noteText OR at facility payment)
          if (completePatientFormik.values.noteText || 
              (completePatientFormik.values.isPayAtFacility && Number(completePatientFormik.values.payAtFacilityAmt) > 0)) {
            await patientRecordUtils.createNotes(
              dispatch, 
              completePatientFormik.values, 
              createdIOC, 
              selectedPatient
            );
          }
          
          // Handle payment if needed
          if (completePatientFormik.values.isPayNow) {
            const clientItem = allClients?.find(client => client.clientId === estimatorState?.clientId);
            const clientCrm = clientItem?.clientCrm || [];
            
            const paymentSuccess = await patientRecordUtils.handlePayment(
              dispatch,
              completePatientFormik.values,
              createdIOC,
              selectedPatient,
              clientCrm,
              stripeToken!,
              channelId,
              handlePatientRecordModalCancel,
              setOwnerId,
              createdIOC
            );
            
            if (!paymentSuccess) {
              setIsLoading(false);
              setEnableSaveButton(true);
              return;
            }
            
            // Update patient contact email AFTER successful payment
            if (completePatientFormik.values.receiptEmail) {
              await updatePatientContactEmail(completePatientFormik.values.receiptEmail);
            }
          } else {
            // Handle integration updates if not making payment
            const clientItem = allClients?.find(client => client.clientId === estimatorState?.clientId);
            const clientCrm = clientItem?.clientCrm || [];
            
            await patientRecordUtils.handleIntegrationUpdates(
              createdIOC,
              clientCrm,
              selectedPatient
            );
          }
          
          // Update payment program
          await patientRecordUtils.updatePaymentProgram(dispatch, createdIOC);
        
        
        setIsLoading(false);
        setEnableSaveButton(true);
        if (estimatorState) {
          handlePatientRecordModalSubmit(estimatorState);
        }
        
        // Fetch the estimates list again before showing the success status
        // This ensures the estimates list is refreshed with the latest data
        if (selectedPatient?.patientId) {
          await dispatch(loadSavedEstimatesListv3({
            paramId: -2,
            patientId: Number(selectedPatient?.patientId),
          }));
        }
        
        dispatch(showStatus(`Successfully created new instance of care`));
      }
    } catch (error) {
      console.error("Error saving patient:", error);
      dispatch(showErrorStatus(`Error saving patient: ${error}`));
      setIsLoading(false);
      setEnableSaveButton(true);
    }
  }

  function handleCancelCallback() {
    handlePatientRecordModalCancel();
  }

  const completePatientSchema = Yup.object(completePatientModalSchema);

  function checkIsFormValid(value: any) {
    completePatientSchema
      .validate(value)
      .then(() => {
      //todo: if the Take Payment Now functionality gets built out on Levels of Care, we need to include that payment amount to the below calculation
        const pfrSummary: any = estimatorState?.summary;
        const pfrAmt = pfrSummary?.totalPFR ?? 0;
        if((pfrAmt - Number(value.payAtFacilityAmt) - Number(value.payNowAmt)) < 0){
          throw new Error('Total payment amount cannot exceed pfr amount');
        }
        setEnableSaveButton(true);
      })
      .catch(() => {
        setEnableSaveButton(false);
      });
  }

  const setEstimatorDescription = (estimatorBody: Estimate) => {
    if (!estimatorBody.description || estimatorBody.description === "") {
      estimatorBody.description = `${vobPatientState.patient.firstName} ${vobPatientState.patient.lastName} - ${(new Date()).toLocaleDateString('en-US', {timeZone: "UTC"})}`
    }
    return estimatorBody;
  }

  const handleStripeStatus = (stripeStatus: boolean) => {
    setStripePaymentReadyStatus(stripeStatus);
  }

  const updatePatientContactEmail = async (receiptEmail: string) => {
    if (!receiptEmail || !selectedPatient?.patientId) {
      return;
    }

    if (receiptEmail === selectedPatient?.contact?.email) {
      return;
    }

    try {
      const patientToSave = {
        contact: {
          firstName: selectedPatient.contact?.firstName || "",
          lastName: selectedPatient.contact?.lastName || "",
          email: receiptEmail,
        },
        patientId: selectedPatient.patientId,
      };

      await dispatch(savePatient({ patient: patientToSave }));
    } catch (error) {
      console.error('Error updating patient contact email:', error);
      dispatch(showErrorStatus(`Error updating patient contact email: ${error}`));
    }
  };

  // Always use selectedPatient for form pre-population
  const patient = selectedPatient.patientId ? selectedPatient : emptyFinPassPatientModel;
  const isSelfPay = useMemo(() => {
      return isSelfPayPayor(selectedPatientCurrentInsurance?.payorId, payersMap);
  }, [selectedPatientCurrentInsurance?.payorId, payersMap]);
  
  // Payor risk: SelfPay (3) if self-pay or private pay, otherwise use rate as payor network status
  const isSelfPayOrPrivatePay = isSelfPay || estimatorState?.isPrivatePay;
  const initialPayorRisk = useMemo(() => {
    if (isSelfPayOrPrivatePay) {
      return 3; // Self-Pay
    }
    return rateAsPayorPlan?.payor?.inNetwork ? 1 : 2; // 1 = In Network, 2 = Out of Network
  }, [isSelfPayOrPrivatePay, rateAsPayorPlan?.payor?.inNetwork]);
  
  const completePatientFormik = useFormik({
    initialValues: {
      patientTitle: patient?.contact?.title || "",
      patientFirstName: patient?.contact?.firstName || "",
      patientMI: patient?.contact?.middleName ? patient?.contact?.middleName : '',
      patientLastName: patient?.contact?.lastName,
      email: patient?.contact?.email ? patient.contact.email : "",
      phone1: patient?.contact.primPhoneNum || "",
      phone1Home: selectedPatient.patientId ? patient?.contact?.primPhoneType ===
          PhoneType.mobile : true,
      phone2: patient?.contact?.secPhoneNum || "",
      phone2Home: patient?.contact?.secPhoneType === PhoneType.mobile ? true : false,
      retypeEmail: patient?.contact?.email ? patient.contact.email : "",
      streetAddress1: patient?.contact?.primaryAddress?.streetAddress1 || "",
      streetAddress2: patient?.contact?.primaryAddress?.streetAddress2 || "",
      city: patient?.contact?.primaryAddress?.city || "",
      stateCode: patient?.contact?.primaryAddress?.state?.stateCode || "",
      zip: patient?.contact?.primaryAddress?.zipCode || "",
      timingRisk: selectedIOC?.timingRisk?.timingRiskId ? selectedIOC.timingRisk.timingRiskId : null, 
      payorRisk: initialPayorRisk,
      isWarmTransfer: "",
      noteText: "",
      isPayNow: false,
      payNowAmt: "",
      isPayAtFacility: false,
      payAtFacilityAmt: "",
      useExistingSource: false,
      savePaymentMethod: false,
      nameOnCard: "",
      leaveVMFl: false,
      smsConsentFl: true,
      paymentReason: "",
      receiptEmail: selectedPatient?.contact?.email || "",
    },
    validate: checkIsFormValid,
    validationSchema: completePatientSchema,
    onSubmit: () => {},
  });

  const channelId = userProfile.userRole.roleType === 'I' ? 5 : 3; //FinAdvisor is 5 (FMS) unless external then its 3 (CRM)

  // const mapToPayment = (totalPaymentAmt: number, paymentProgramResponse: any) => {
  //   const scheduleId = paymentProgramResponse.selectedPatientEncounter.patientPaymentProgram[0].patientPaymentSchedule.patientPaymentScheduleId;
  //   const currentDateTime = new Date();
  //   return {
  //     isAuthExpired: false,
  //     isAuthOnly: false,
  //     isManual: true,
  //     patientEncounterId: selectedIOC?.patientEncounterId,
  //     patientId: selectedPatient.patientId,
  //     patientPaymentScheduleId: scheduleId,
  //     paymentAmt: totalPaymentAmt,
  //     paymentGroupId: scheduleId.toString(),
  //     paymentId: 0,
  //     paymentInitDt: currentDateTime.toISOString(),
  //     paymentMethods: [{
  //       externalPaymentMethodId: 'card',
  //       paymentMethodTypeId: 1,
  //       externalPaymentId: stripeToken,
  //       payorId: '',
  //       nameOnCard: completePatientFormik.values.nameOnCard
  //     }],
  //     paymentType: PAYMENT_TYPES.DOWN_PAYMENT,
  //     receiptEmail: completePatientFormik.values.email,
  //     paymentChannelId: channelId,
  //     isFinAdvisor: true,
  //     ...(completePatientFormik.values.paymentReason ? { paymentReason: completePatientFormik.values.paymentReason } : {}),
  //     ...(ownerId && completePatientFormik.values.isPayNow ? { ownerId } : {}),
  //   } as Payment;
  // }

  // const mapToPatientPaymentProgram = useCallback(() => {
  //   const pfrSummary: any = estimatorState?.summary;
  //   const pfrAmt = pfrSummary.totalPFR;
  //   const currentDate = new Date();
  //   const downPaymentAmt = 0;
  //   const terms = 0;
  //   const recurringPaymentAmt = 0;

  //   let patientPaymentSchedule: Partial<PatientPaymentSchedule>;

  //   if (selectedIOC?.patientPaymentProgram?.[0]?.patientPaymentSchedule) {
  //     const {
  //       downPmtAmt,
  //       paymentDueAmt,
  //       paymentFreq,
  //       pfrAmt: existingPfrAmt,
  //       remainingTerms,
  //       scheduleStartDt,
  //       scheduleStatus,
  //       terms: existingTerms
  //     } = selectedIOC.patientPaymentProgram[0].patientPaymentSchedule;

  //     patientPaymentSchedule = {
  //       downPmtAmt,
  //       paymentDueAmt,
  //       paymentFreq,
  //       pfrAmt: existingPfrAmt,
  //       remainingTerms,
  //       scheduleStartDt,
  //       scheduleStatus,
  //       terms: existingTerms
  //     };
  //   } else {
  //     patientPaymentSchedule = {
  //       pfrAmt: pfrAmt,
  //       paymentFreq: 'M',
  //       downPmtAmt: downPaymentAmt,
  //       terms: terms,
  //       remainingTerms: terms,
  //       paymentDueAmt: recurringPaymentAmt,
  //       scheduleStatus: 'Pending',
  //       scheduleStartDt: currentDate.toISOString(),
  //     };
  //   }

  //   const paymentProgram = {
  //     patientPaymentProgramId: selectedIOC?.patientPaymentProgram?.[0]?.patientPaymentProgramId || 0,
  //     isHighRisk: false,
  //     isPaidInFull: false,
  //     downPmtAmt: downPaymentAmt,
  //     areDocsSigned: false,
  //     patientPaymentSchedule: patientPaymentSchedule,
  //   } as PatientPaymentProgram
  //   return paymentProgram;
  // }, [selectedIOC])

  return (
    <Dialog
      className="modal"
      open={open}
      fullWidth={true}
      maxWidth="md"
      scroll="body"
      test-id="complete-patient-record-modal"
    >
      { isLoading && 
        <Grid item xs={12} className="inline-modal-loader">
          <LoadingOverlay whiteBackground />
        </Grid>
      }
      <DialogTitle>Complete Patient Record</DialogTitle>
      <DialogContent>
        <p>
          Please complete the following form before submitting this estimate to
          FinPass.
        </p>
        <Typography variant="subtitle2" className="my-3" style={{marginTop: '22px'}}>
          Instance of Care Info
        </Typography>
        <Grid container spacing={2}>
          <Grid item direction="column" md={4} sm={4}>
            <TextField
              select
              error={
                completePatientFormik.touched["timingRisk"] &&
                completePatientFormik.errors["timingRisk"]
                  ? completePatientFormik.errors["timingRisk"]
                  : ""
              }
              label="Timing Risk"
              name="timingRisk"
              test-id='complete-patient-record-timing-risk-dropdown'
              value={completePatientFormik.values.timingRisk}
              onChange={(e: Event) => {
                completePatientFormik.handleChange(e);
              }}
              onBlur={completePatientFormik.handleBlur}
            >
              {timingRisk.map((risk) => (
                <MenuItem key={risk.timingRiskId} value={risk.timingRiskId} test-id={`complete-patient-record-timing-risk-option-${risk.timingRiskId}`}>
                  {risk.timingRiskName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item direction="column" md={4} sm={4}>
            <TextField
              select={true}
              test-id='complete-patient-record-payor-risk'
              error={
                completePatientFormik.touched["payorRisk"] &&
                completePatientFormik.errors["payorRisk"]
                  ? completePatientFormik.errors["payorRisk"]
                  : ""
              }
              label="Payer Risk"
              name="payorRisk"
              value={completePatientFormik.values.payorRisk}
              onChange={completePatientFormik.handleChange}
              onBlur={completePatientFormik.handleBlur}
            >
              {payorRisk.map((payor: {payorRiskName: string, payorRiskId: number}) => (
                <MenuItem key={payor.payorRiskId} value={payor.payorRiskId} test-id={`complete-patient-record-payor-risk-option-${payor.payorRiskId}`}>
                  {payor.payorRiskName}
                </MenuItem>
              ))}
              </TextField>
          </Grid>

          <Grid item direction="column" md={4} sm={4}>
            <TextField
              error={
                completePatientFormik.touched["isWarmTransfer"] &&
                completePatientFormik.errors["isWarmTransfer"]
                  ? completePatientFormik.errors["isWarmTransfer"]
                  : ""
              }
              select={true}
              test-id='complete-patient-record-interaction-type'
              label='Interaction Type'
              name="isWarmTransfer"
              value={completePatientFormik.values.isWarmTransfer}
              onChange={completePatientFormik.handleChange}
              onBlur={completePatientFormik.handleBlur}
            >
              <MenuItem value={'Warm Transfer'} test-id="complete-patient-record-interaction-type-option-warm-transfer">Warm Transfer</MenuItem>
              <MenuItem value={'Inbound'} test-id="complete-patient-record-interaction-type-option-inbound">Inbound Call</MenuItem>
              <MenuItem value={'Outbound'} test-id="complete-patient-record-interaction-type-option-outbound">Outbound Call</MenuItem>
            </TextField>
          </Grid>

          <Grid item direction="column" md={12}  style={{marginTop: '6px'}}>
            <TextField
              error={
                completePatientFormik.touched["noteText"] &&
                completePatientFormik.errors["noteText"]
                  ? completePatientFormik.errors["noteText"]
                  : ""
              }
              label="Notes"
              name="noteText"
              multiline
              value={
                completePatientFormik.values.noteText
              }
              placeholder=""
              onChange={completePatientFormik.handleChange}
              onBlur={completePatientFormik.handleBlur}
              />
          </Grid>
        </Grid>
        <Typography variant="subtitle2" className="my-3" style={{marginTop: '22px'}}>
          Estimation Details
        </Typography>
        <EstDetails/>
        {finadvisorTakePayment && !isBillingEngineV3 && (
          <TakePaymentView 
            formik={completePatientFormik}
            handleStripeStatus={handleStripeStatus}
          />
        )}
      </DialogContent>
      <DialogActions
        test-id="complete-patient-record-dialog-actions"
      >
        <DialogActionButton
          // isEnabled={completePatientFormik.values.isPayNow ? (!displayPFRTotalError && stripePaymentReadyStatus && enableSaveButton) : enableSaveButton}
          isEnabled={true}
          savebuttonText="Save"
          saveStatus={iocSaveStatus}
          spinnerLeftPosition={5}
          executeSave={handleSave}
          handleCallbackSave={() => {}}
          handleCallbackCancel={handleCancelCallback}
          test-id="complete-patient-record-save-button"
        />
      </DialogActions>
    </Dialog>
  );
}


